import type { OfferVersionDto } from '@fos/shared/contracts/offers';
import { OFFER_FIELD_LABELS } from '@fos/shared/labels';
import { FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatDuration } from '../../../lib/format';
import { Markdown } from '../../../ui/Markdown';
import { Mono } from '../../../ui/Mono';
import { EmptyState } from '../../../ui/States';
import { isContentEmpty, offerRateText } from '../offer-format';

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} className="flex flex-col gap-2 border-b border-line pb-5 last:border-b-0">
      <h2 className="text-caption font-medium text-fg-muted">{label}</h2>
      {children}
    </section>
  );
}

/**
 * Treść wersji oferty sekcjami (widok czytania): zakres, rezultat, wyłączenia, założenia (Markdown), szacowany
 * czas albo pakiet, stawka (tylko gdy jest w DTO, czyli z „Widzi stawki i kwoty”), termin, warunki, przykłady.
 * Puste sekcje pomijamy.
 */
export function OfferContentView({ version, emptyAction }: { version: OfferVersionDto; emptyAction?: ReactNode }) {
  const c = version.content;
  if (isContentEmpty(c) && !c.estimatedSeconds && !c.timeline) {
    return (
      <EmptyState
        icon={FileText}
        title="Oferta jest jeszcze pusta"
        description="Uzupełnij zakres, rezultat i warunki współpracy."
        action={emptyAction}
      />
    );
  }
  const md = (key: 'scope' | 'result' | 'exclusions' | 'assumptions' | 'terms') =>
    c[key].trim() ? (
      <Section key={key} label={OFFER_FIELD_LABELS[key]}>
        <Markdown className="text-body-s">{c[key]}</Markdown>
      </Section>
    ) : null;

  const facts: Array<{ label: string; value: ReactNode }> = [];
  if (c.package) {
    facts.push({
      label: OFFER_FIELD_LABELS.package,
      value: (
        <>
          {c.estimatedSeconds ? <Mono>{formatDuration(c.estimatedSeconds)}</Mono> : null}
          {c.estimatedSeconds ? ' · ' : ''}
          {c.package}
        </>
      ),
    });
  } else if (c.estimatedSeconds) {
    facts.push({
      label: OFFER_FIELD_LABELS.estimatedSeconds,
      value: <Mono>{formatDuration(c.estimatedSeconds)}</Mono>,
    });
  }
  if (c.rate) facts.push({ label: 'Stawka netto', value: <Mono>{offerRateText(c.rate, c.currency)}</Mono> });
  if (c.timeline) facts.push({ label: OFFER_FIELD_LABELS.timeline, value: c.timeline });

  return (
    <div className="flex flex-col gap-5">
      {md('scope')}
      {md('result')}
      {md('exclusions')}
      {md('assumptions')}
      {facts.length > 0 && (
        <Section label="Czas i termin">
          <dl className="grid gap-x-6 gap-y-2 text-body-s sm:grid-cols-[180px_minmax(0,1fr)]">
            {facts.map((f) => (
              <div key={f.label} className="contents">
                <dt className="text-fg-muted">{f.label}</dt>
                <dd className="text-fg">{f.value}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
      {md('terms')}
      {c.examples?.trim() ? (
        <Section label={OFFER_FIELD_LABELS.examples}>
          <Markdown className="text-body-s">{c.examples}</Markdown>
        </Section>
      ) : null}
    </div>
  );
}
